export const settingsStrings = {
  // ── Auth ─────────────────────────────────────────────────────────────────────
  authSignOut: 'Cerrar sesión',
  authSigningOut: 'Cerrando sesión...',

  // ── Page ─────────────────────────────────────────────────────────────────────
  pageTitle: 'Ajustes',
  accountSectionTitle: 'Mi cuenta',
  accountNameLabel: 'Nombre',
  accountEmailLabel: 'Correo electrónico',
  accountSpecialtyLabel: 'Especialidad',
  accountLicenseLabel: 'No. de licencia',

  // ── Profile edit ─────────────────────────────────────────────────────────────
  profileEditTitle: 'Editar perfil',
  editButton: 'Editar',
  cancelButton: 'Cancelar',
  saveButton: 'Guardar cambios',
  savingButton: 'Guardando...',
  notSet: 'Sin definir',
  profileUpdateError: 'No se pudo actualizar el perfil. Intenta de nuevo.',

  // ── Menu links ────────────────────────────────────────────────────────────────
  locationsTitle: 'Ubicaciones',
  locationsDescription: 'Centros médicos y consultorios donde ejerces',
  templatesDescription: 'Gestiona las plantillas de protocolos de tu práctica',
  typesDescription: 'Categorías que agrupan tus protocolos',
  auditLogTitle: 'Registros de actividad',
  auditLogDescription: 'Historial de todas las acciones del sistema',
  schedulesTitle: 'Horario de disponibilidad',
  schedulesDescription: 'Bloques semanales y excepciones por ubicación',
  usersTitle: 'Usuarios',
  usersDescription: 'Gestiona el equipo de tu institución',
  securityTitle: 'Seguridad',
  securityDescription: 'Accesos, dispositivos y actividad de inicio de sesión',
} as const

export const usersStrings = {
  pageTitle: 'Usuarios',
  newButton: 'Nuevo usuario',
  emptyTitle: 'Sin usuarios',
  emptyDescription: 'Invita al primer miembro de tu equipo.',
  noAccessTitle: 'Sin acceso',
  noAccessDescription: 'No tienes permiso para gestionar usuarios.',
  loading: 'Cargando usuarios...',
  loadError: 'No se pudieron cargar los usuarios.',
  tableName: 'Nombre',
  tableEmail: 'Correo',
  tableRole: 'Rol',
  tableStatus: 'Estado',
  statusActive: 'Activo',
  statusInactive: 'Inactivo',
  statusInvited: 'Invitación enviada',
  activateButton: 'Activar',
  deactivateButton: 'Desactivar',
  formTitle: 'Nuevo usuario',
  nameLabel: 'Nombre completo',
  namePlaceholder: 'Ej. Ana Reyes',
  emailLabel: 'Correo electrónico',
  emailPlaceholder: 'usuario@clinica.do',
  roleLabel: 'Rol',
  roleAssistant: 'Asistente',
  roleDoctor: 'Doctor',
  roleAdmin: 'Administrador',
  roleSuperAdmin: 'Propietario',
  cancelButton: 'Cancelar',
  createButton: 'Crear usuario',
  creatingButton: 'Creando...',
  createError: 'No se pudo crear el usuario. Intenta de nuevo.',
  createSuccess: 'Usuario creado. Se envió un enlace para crear la contraseña.',
  roleChangeLabel: 'Cambiar rol',
  roleChangeError: 'No se pudo cambiar el rol. Intenta de nuevo.',
  resendButton: 'Reenviar invitación',
  resendingButton: 'Reenviando...',
  resendError: 'No se pudo reenviar la invitación. Intenta de nuevo.',
} as const

export const permissionsStrings = {
  pageTitle: 'Permisos',
  menuTitle: 'Permisos',
  menuDescription: 'Define qué puede ver y gestionar cada rol',
  loading: 'Cargando permisos...',
  loadError: 'No se pudieron cargar los permisos. Intenta recargar la página.',
  colModule: 'Módulo',
  // Role column headers (super_admin replaces the old "owner")
  roleAssistant: 'Asistente',
  roleDoctor: 'Doctor',
  roleAdmin: 'Administrador',
  roleSuperAdmin: 'Propietario',
  // Access levels
  levelNone: 'Sin acceso',
  levelView: 'Ver',
  levelManage: 'Gestionar',
  levelMixed: 'Mixto',
  // Sections
  sectionClinical: 'Trabajo clínico',
  sectionAdmin: 'Administración',
  sectionApplyAll: 'Aplicar a toda la sección',
  readOnlyNotice: 'Solo puedes editar los permisos de roles por debajo del tuyo.',
  // Module display names (Spanish labels for each ModuleKey in the catalog)
  moduleLabels: {
    patients: 'Pacientes',
    consultations: 'Consultas',
    protocols: 'Protocolos',
    appointments: 'Citas',
    orders: 'Órdenes',
    billing: 'Facturación',
    locations: 'Ubicaciones',
    templates: 'Plantillas',
    categories: 'Categorías',
    schedules_config: 'Horarios',
    audit_log: 'Registros de actividad',
    users: 'Usuarios',
    permissions: 'Permisos',
  },
} as const

export const templatesStrings = {
  pageTitle: 'Plantillas',
  newButton: 'Nueva plantilla',
  emptyTitle: 'Sin plantillas',
  emptyDescription: 'Crea tu primera plantilla para empezar a diseñar protocolos.',
  listSeeded: 'Predeterminada',
  listSeededBadge: 'Sistema',
  listLocked: 'Bloqueada',
  listBlockedBy: (count: number) => `Bloqueada por ${count} tipo${count === 1 ? '' : 's'}`,
  listCategory: 'Categoría',
  listEdit: 'Editar',
  listDelete: 'Eliminar',
  listDeleteConfirm: (name: string) =>
    `¿Eliminar la plantilla "${name}"? Esta acción no se puede deshacer.`,
  loading: 'Cargando plantillas...',
  error: 'No se pudo cargar las plantillas.',
  deleteSuccess: 'Plantilla eliminada.',
  deleteLocked: 'No se puede eliminar: tiene tipos asociados.',
  deleteSeeded: 'No se puede eliminar una plantilla del sistema.',
} as const

export const templateEditorStrings = {
  newTitle: 'Nueva plantilla',
  save: 'Guardar plantilla',
  saving: 'Guardando...',
  cancel: 'Cancelar',
  saved: 'Plantilla guardada.',
  unsaved: 'Cambios sin guardar',
  fieldName: 'Nombre de la plantilla',
  fieldNamePlaceholder: 'Ej. Intervención de emergencia',
  statusNew: 'Nueva',
  statusEdited: 'Editada',
  statusLocked: 'Bloqueada',
  lockedBanner:
    'Esta plantilla está bloqueada por uno o más tipos. Elimínalos primero para editarla.',
  lockedTypesPrefix: 'Bloqueada por:',
  addSection: '+ Sección',
  addText: '+ Texto',
  addChecklist: '+ Checklist',
  addSteps: '+ Pasos',
  addDecision: '+ Decisión',
  addDosage: '+ Tabla dosis',
  addAlert: '+ Alerta',
  needsSection: 'Añade primero una sección para contener este bloque.',
  requiredLabel: 'Requerido',
  requiredSectionLabel: 'Requerida',
  deleteSectionConfirm: (title: string, count: number) =>
    `¿Eliminar la sección "${title}" y sus ${count} bloque${count === 1 ? '' : 's'} hijo${count === 1 ? '' : 's'}?`,
  placeholderHint: 'Pista para el médico (opcional)',
  sectionTitleLabel: 'Título de la sección',
  sectionDescLabel: 'Descripción (opcional)',
  navigateAway: 'Tienes cambios sin guardar. ¿Descartar y salir?',
  moveUp: 'Mover arriba',
  moveDown: 'Mover abajo',
  duplicate: 'Duplicar',
  delete: 'Eliminar',
  requiredTooltip: 'Desactiva "Requerido" antes de eliminar.',
  noTitle: '(sin título)',
} as const

export const typesStrings = {
  pageTitle: 'Categorías de protocolo',
  newButton: 'Nueva categoría',
  emptyTitle: 'Sin categorías',
  emptyDescription: 'Crea categorías para organizar tus protocolos.',
  loading: 'Cargando categorías...',
  error: 'No se pudo cargar las categorías.',
  listEdit: 'Editar',
  listDelete: 'Eliminar',
  listDeleteConfirm: (name: string) =>
    `¿Eliminar la categoría "${name}"? Esta acción no se puede deshacer.`,
  deleteSeeded: 'No se puede eliminar una categoría del sistema.',
  deleteBlockedTitle: 'No se puede eliminar esta categoría',
  deleteBlockedBody: (count: number) =>
    `No puedes eliminar esta categoría: ${count} plantilla${count === 1 ? '' : 's'} la usan. Reasígnalas a otra categoría antes de eliminarla.`,
  deleteBlockedClose: 'Entendido',
  createTitle: 'Nueva categoría',
  createFieldName: 'Nombre',
  createFieldNamePlaceholder: 'Ej. Emergencia, Procedimiento',
  createFieldColor: 'Color',
  createFieldSpecialty: 'Especialidad sugerida',
  createFieldSpecialtyPlaceholder: 'Ej. cardiología, pediatría',
  createSubmit: 'Crear categoría',
  createSubmitting: 'Creando...',
  createCancel: 'Cancelar',
  editTitle: 'Editar categoría',
  editFieldName: 'Nombre',
  editFieldColor: 'Color',
  editFieldSpecialty: 'Especialidad sugerida',
  editFieldSpecialtyPlaceholder: 'Ej. cardiología, pediatría',
  editSubmit: 'Guardar',
  editSubmitting: 'Guardando...',
  editCancel: 'Cancelar',
  seededBadge: 'Sistema',
  activeBadge: 'Activa',
} as const

export const locationsStrings = {
  pageTitle: 'Ubicaciones',
  newButton: 'Nueva ubicación',
  loading: 'Cargando ubicaciones...',
  loadError: 'No se pudieron cargar las ubicaciones. Intenta recargar la página.',
  deleteError: 'No se puede eliminar: la ubicación tiene citas próximas programadas.',
  emptyTitle: 'Aún no tienes ubicaciones registradas',
  emptyDescription:
    'Registra los centros médicos o consultorios donde ejerces para poder agendar citas.',
  tableHeaderName: 'Nombre',
  tableHeaderAddress: 'Dirección',
  tableHeaderPhone: 'Teléfono',
  tableHeaderFee: 'Honorarios',
  tableHeaderCommission: 'Comisión',
  tableHeaderType: 'Tipo',
  editButton: 'Editar',
  deleteButtonTitle: 'Eliminar ubicación',
  ownedBadge: 'Propio',
  externalBadge: 'Externo',
  formCreateTitle: 'Nueva ubicación',
  formEditTitle: 'Editar ubicación',
  nameLabel: 'Nombre',
  namePlaceholder: 'Ej. Centro Médico Lincoln',
  nameRequired: 'El nombre es obligatorio (mínimo 2 caracteres).',
  cityLabel: 'Ciudad',
  cityPlaceholder: 'Ej. Santo Domingo',
  phoneLabel: 'Teléfono',
  phonePlaceholder: 'Ej. 809-555-0000',
  addressLabel: 'Dirección',
  addressPlaceholder: 'Ej. Av. Winston Churchill 1099, Suite 301',
  feeLabel: 'Honorarios (RD$)',
  feeHelper: 'Tu tarifa de consulta en esta ubicación',
  commissionLabel: 'Comisión (%)',
  commissionHelper: 'Porcentaje que retiene el centro',
  typeLabel: 'Tipo',
  ownedCheckbox: 'Consultorio propio',
  notesLabel: 'Notas',
  notesPlaceholder: 'Información adicional sobre esta ubicación...',
  updateError: 'No se pudo actualizar la ubicación. Intenta de nuevo.',
  createError: 'No se pudo crear la ubicación. Intenta de nuevo.',
  cancelButton: 'Cancelar',
  savingButton: 'Guardando...',
  saveButton: 'Guardar cambios',
  createButton: 'Crear ubicación',
  deleteTitle: 'Eliminar ubicación',
  deleteBody: (name: string) => `¿Eliminar ${name}? Esta acción no se puede deshacer.`,
  deleteConfirmButton: 'Eliminar ubicación',
  deletingButton: 'Eliminando...',
  archiveButtonTitle: 'Archivar ubicación',
  archiveTitle: 'Archivar ubicación',
  archiveBody: (name: string) =>
    `¿Archivar ${name}? La ubicación ya no aparecerá en tu agenda ni en nuevas citas.`,
  archiveConfirmButton: 'Archivar',
  archivingButton: 'Archivando...',
  archiveError: 'No se puede archivar: la ubicación tiene citas próximas programadas.',
} as const

export const auditLogStrings = {
  pageTitle: 'Registros de actividad',
  exportButton: 'Exportar CSV',
  exportingButton: 'Exportando...',
  exportError: 'No se pudo generar el CSV. Intenta de nuevo.',
  loading: 'Cargando registros...',
  loadError: 'No se pudieron cargar los registros. Intenta recargar la página.',
  emptyTitle: 'Aún no hay actividad registrada',
  emptyDescription:
    'Cuando uses el sistema, aparecerá aquí cada acción — consultas firmadas, citas creadas, emails enviados.',

  // Table headers
  colDate: 'Fecha',
  colActor: 'Actor',
  colCategory: 'Categoría',
  colAction: 'Acción',
  colEntity: 'Entidad',
  colStatus: 'Estado',

  // Pagination
  recordCount: (count: number) => `${count} registro${count !== 1 ? 's' : ''}`,
  firstPage: 'Primera página',
  nextPage: 'Siguiente →',

  // Plan banner
  planBannerDays: (days: number) => `Estás viendo los últimos ${days} días.`,
  planBannerUpgrade: 'Actualiza tu plan para ver el historial completo.',

  // Filters
  filterFrom: 'Desde',
  filterTo: 'Hasta',
  filterAllCategories: 'Todas las categorías',
  filterAllStatuses: 'Todos los estados',
  filterStatusSuccess: 'Exitoso',
  filterStatusFailed: 'Fallido',
  filterClearButton: 'Limpiar filtros',

  // Category labels
  categoryEntity: 'Entidad',
  categoryAuth: 'Autenticación',
  categoryCommunication: 'Comunicación',
  categorySystem: 'Sistema',

  // Action labels
  actionCreate: 'Creado',
  actionUpdate: 'Actualizado',
  actionDelete: 'Eliminado',
  actionRestore: 'Restaurado',
  actionSign: 'Firmado',
  actionAmend: 'Enmendado',
  actionArchive: 'Archivado',
  actionLock: 'Bloqueado',
  actionUnlock: 'Desbloqueado',
  actionLogin: 'Inicio de sesión',
  actionLogout: 'Cierre de sesión',
  actionLoginFailed: 'Inicio de sesión fallido',
  actionPasswordChange: 'Cambio de contraseña',
  actionMfaEnabled: 'MFA habilitado',
  actionMfaPolicyChanged: 'Política de MFA actualizada',
  actionSessionRevoked: 'Sesión revocada',
  actionPermissionGranted: 'Permiso otorgado',
  actionPermissionRevoked: 'Permiso revocado',
  actionRoleChanged: 'Rol cambiado',
  actionUserInvited: 'Usuario invitado',
  actionUserDeactivated: 'Usuario desactivado',
  actionUserReactivated: 'Usuario reactivado',
  actionEmailQueued: 'Email en cola',
  actionEmailSent: 'Email enviado',
  actionEmailDelivered: 'Email entregado',
  actionEmailBounced: 'Email rechazado',
  actionSmsSent: 'SMS enviado',
  actionWhatsappSent: 'WhatsApp enviado',
  actionNotificationSent: 'Notificación enviada',
  actionPdfGenerated: 'PDF generado',
  actionPdfDownloaded: 'PDF descargado',
  actionReminderSent: 'Recordatorio enviado',
  actionInvoiceIssued: 'Factura emitida',
  actionPrescriptionDispensed: 'Receta dispensada',
  actionExportGenerated: 'Exportación generada',
  actionReportRun: 'Informe ejecutado',
  actionBackupVerified: 'Respaldo verificado',
  actionWebhookReceived: 'Webhook recibido',

  // Actor types
  actorSystem: 'Sistema',
  actorWebhook: 'Webhook',
  actorCron: 'Cron',
  actorUnknown: 'Usuario desconocido',
  actorUser: 'Usuario',

  // Status
  statusFailed: 'Fallido',

  // Detail drawer
  drawerCloseLabel: 'Cerrar',
  drawerActorSection: 'Actor',
  drawerEntitySection: 'Entidad',
  drawerChangesSection: 'Cambios',
  drawerMetadataSection: 'Detalles',
  drawerRequestIdSection: 'ID de solicitud',
} as const

export const schedulesStrings = {
  pageTitle: 'Horario de disponibilidad',
  noLocations: 'No tienes ubicaciones registradas',
  noLocationsDescription: 'Añade una ubicación para gestionar tu horario de disponibilidad.',
  goToLocationsButton: 'Ir a Ubicaciones',
  weeklyBlocksTitle: 'Disponibilidad semanal',
  addBlockButton: 'Añadir bloque',
  exceptionsTitle: 'Excepciones de horario',
  addExceptionButton: 'Añadir excepción',
  loading: 'Cargando…',
  noBlocks: 'Sin bloques de disponibilidad',
  noBlocksDescription: 'Añade un bloque para definir tu horario semanal.',
  noExceptions: 'Sin excepciones registradas',
  noExceptionsDescription:
    'Añade excepciones para días festivos, vacaciones o disponibilidad extra.',
  deleteBlockLabel: 'Eliminar bloque',
  deleteExceptionLabel: 'Eliminar excepción',
  exceptionBlocked: 'Bloqueado',
  exceptionAvailable: 'Disponible extra',
  slotDurationSuffix: 'min/turno',

  // Block form modal
  blockFormTitle: 'Nuevo bloque de disponibilidad',
  blockDayLabel: 'Día de la semana',
  blockStartTimeLabel: 'Hora inicio',
  blockEndTimeLabel: 'Hora fin',
  blockSlotDurationLabel: 'Duración por turno (min)',
  blockSlotDurationHelper: 'Ej. 30 o 60 minutos',
  blockOverlapError: 'Ya existe un bloque en ese horario para ese día.',
  blockTimeInvalidError: 'La hora de inicio debe ser anterior a la hora de fin.',
  blockCreateError: 'No se pudo crear el bloque. Intenta de nuevo.',
  blockCancelButton: 'Cancelar',
  blockSavingButton: 'Guardando…',
  blockCreateButton: 'Crear bloque',

  // Exception form modal
  exceptionFormTitle: 'Nueva excepción de horario',
  exceptionDateLabel: 'Fecha',
  exceptionTypeLabel: 'Tipo',
  exceptionTypeBlocked: 'Bloqueado',
  exceptionTypeAvailable: 'Disponible extra',
  exceptionStartTimeLabel: 'Hora inicio',
  exceptionStartTimeHelper: 'Opcional',
  exceptionEndTimeLabel: 'Hora fin',
  exceptionEndTimeHelper: 'Opcional',
  exceptionReasonLabel: 'Motivo',
  exceptionReasonHelper: 'Opcional',
  exceptionReasonPlaceholder: 'Ej. Día festivo',
  exceptionTimeInvalidError: 'La hora de inicio debe ser anterior a la hora de fin.',
  exceptionCreateError: 'No se pudo crear la excepción. Intenta de nuevo.',
  exceptionCancelButton: 'Cancelar',
  exceptionSavingButton: 'Guardando…',
  exceptionCreateButton: 'Crear excepción',

  // Day labels
  dayMonday: 'Lunes',
  dayTuesday: 'Martes',
  dayWednesday: 'Miércoles',
  dayThursday: 'Jueves',
  dayFriday: 'Viernes',
  daySaturday: 'Sábado',
  daySunday: 'Domingo',
} as const

export const securityStrings = {
  pageTitle: 'Seguridad',
  pageSubtitle: 'Actividad de acceso de tu institución.',
  statLogins: (days: number) => `Accesos ${days}d`,
  statDistinctUsers: (days: number) => `Usuarios distintos ${days}d`,
  statDormant: 'Sin acceso 30d',
  statMfaAdoption: 'Adopción MFA',
  policyCardTitle: 'Política de acceso',
  policyCardDescription: 'Define si la autenticación de dos factores es obligatoria.',
  policyOptionOff: 'Opcional para todos',
  policyOptionAdmins: 'Obligatorio para administradores',
  policyOptionAll: 'Obligatorio para todos',
  policySaveButton: 'Guardar',
  policySavingButton: 'Guardando...',
  policySaveSuccess: 'Política actualizada.',
  policySaveError: 'No se pudo actualizar la política.',
  policyEnforcementNote:
    'Este ajuste aún no bloquea el inicio de sesión; solo queda registrado para uso futuro.',
  rangeLabel: 'Rango',
  range7: '7 días',
  range30: '30 días',
  exportButton: 'Exportar',
  exportingButton: 'Exportando...',
  exportError: 'No se pudo exportar el archivo CSV.',
  tableDate: 'Fecha',
  tableUser: 'Usuario',
  tableMethod: 'Método',
  tableIp: 'IP / dispositivo',
  tableOutcome: 'Resultado',
  methodPassword: 'Contraseña',
  methodGoogle: 'Google',
  methodSso: 'SSO',
  methodUnknown: 'Desconocido',
  outcomeSuccess: 'Exitoso',
  outcomeBlocked: 'Bloqueado',
  unknownUser: 'Usuario desconocido',
  loading: 'Cargando actividad...',
  loadError: 'No se pudo cargar la actividad de acceso.',
  emptyTitle: 'Sin actividad',
  emptyDescription: 'No hay accesos registrados en este rango.',
} as const

export const profileDevicesStrings = {
  sectionTitle: 'Dispositivos',
  sectionDescription: 'Dispositivos donde has iniciado sesión.',
  lastSeen: (date: string) => `Última vez: ${date}`,
  emptyText: 'Aún no se ha registrado ningún dispositivo.',
  signOutAllButton: 'Cerrar todas las sesiones',
  confirmTitle: 'Cerrar todas las sesiones',
  confirmDescription:
    'Se cerrará tu sesión en todos los dispositivos, incluido este. Deberás iniciar sesión de nuevo.',
  confirmButton: 'Cerrar sesiones',
  cancelButton: 'Cancelar',
  signOutError: 'No se pudieron cerrar las sesiones. Intenta de nuevo.',
  loading: 'Cargando dispositivos...',
} as const

export const profileMfaStrings = {
  sectionTitle: 'Autenticación en dos pasos',
  sectionDescription: 'Protege tu cuenta con un código de tu aplicación de autenticación.',
  statusEnrolled: 'Activada',
  statusNotEnrolled: 'No activada',
  configureButton: 'Configurar',
  removeButton: 'Quitar',
  cancelButton: 'Cancelar',
  scanInstructions:
    'Agrega esta clave a tu aplicación de autenticación (Google Authenticator, Authy, etc.), luego ingresa el código de 6 dígitos que genera.',
  otpauthUrlLabel: 'URL de configuración',
  secretLabel: 'Clave secreta',
  fieldCode: 'Código de verificación',
  fieldCodePlaceholder: '123456',
  verifyButton: 'Verificar y activar',
  enrollError: 'No se pudo iniciar la configuración. Intenta de nuevo.',
  verifyError: 'Código inválido. Verifica e intenta de nuevo.',
  removeError: 'No se pudo quitar la autenticación en dos pasos.',
  removeConfirmTitle: 'Quitar autenticación en dos pasos',
  removeConfirmDescription:
    'Tu cuenta ya no requerirá un código de verificación al iniciar sesión.',
  removeConfirmButton: 'Quitar',
} as const
